import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useMyNotes, useSearchNotes } from '@/hooks/useMyNotes';
import { NoteCard } from './NoteCard';
import { useNotepadEditor } from './useNotepadEditorState';
import { Input } from '@/components/ui/input';
import { Search, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

/**
 * Notes tab - search, filter, list notes
 * Opens note inline (split pane) for editing
 */
export function NotesTab() {
  const { t } = useTranslation(['notepad', 'common']);
  const [searchText, setSearchText] = useState('');
  const [selectedNoteId, _setSelectedNoteId] = useState<string | null>(null);
  const { openCreate, openEdit } = useNotepadEditor();

  // Draft-first: open create editor without Firestore write
  const handleCreateNote = useCallback(() => {
    openCreate('note');
  }, [openCreate]);

  // Keyboard shortcut: Cmd/Ctrl + N to create new note
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        handleCreateNote();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreateNote]);

  // Use search if text provided, otherwise list all
  const { data: searchResults } = useSearchNotes(searchText, {
    archived: false,
  });

  const { data: allNotes, isLoading } = useMyNotes({
    archived: false,
    pinnedFirst: true,
    limit: 50,
  });

  const notes = searchText ? searchResults : allNotes;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">
          {t('common:loading', 'Loading notes...')}
        </div>
      </div>
    );
  }

  const hasNotes = notes && notes.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header with New Note button */}
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('notepad:search.notes', 'Search notes...')}
              className="pl-9"
              aria-label={t('common:accessibility.searchNotes')}
            />
          </div>

          {/* New Note button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={handleCreateNote}
              className="gap-2 shadow-sm"
              aria-label={t('common:accessibility.createNoteShortcut')}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('notepad:actions.addNote', 'New Note')}</span>
            </Button>
          </motion.div>
        </div>

        {/* Divider */}
        <Separator />
      </div>

      {/* Content area with scroll */}
      <div className="flex-1 overflow-y-auto -mx-4 px-4">
        {/* Empty State */}
        <AnimatePresence mode="wait">
          {!hasNotes && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: 'spring' }}
              >
                {searchText ? (
                  <Search className="h-16 w-16 text-muted-foreground/30 mb-6" />
                ) : (
                  <Sparkles className="h-16 w-16 text-primary/30 mb-6" />
                )}
              </motion.div>
              <h3 className="text-xl font-semibold mb-2">
                {searchText
                  ? t('notepad:empty.noResults', 'No notes found')
                  : t('notepad:empty.notes', 'No notes yet')}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mb-1">
                {searchText
                  ? t('notepad:empty.tryDifferent', 'Try a different search term')
                  : t('notepad:empty.startWriting', 'Start by writing your first thought')}
              </p>
              {!searchText && (
                <p className="text-xs text-muted-foreground/70">
                  Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">⌘N</kbd>{' '}
                  {t('notepad:empty.createNoteHint')}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notes List */}
        {hasNotes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            <AnimatePresence>
              {notes.map((note, index) => (
                <motion.div
                  key={note.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                >
                  <NoteCard
                    note={note}
                    isSelected={selectedNoteId === note.id}
                    onClick={() => openEdit('note', note.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
}
